// Implements: F-IDN-014, F-UIX-009, F-UIX-010
/**
 * The Verificación en dos pasos section: owns the profile read (is it on, since when), the
 * enrolment, the verification and the disabling, and decides what each answer means.
 *
 * The enrolment's secret lives ONLY in this container's state: it is taken out of the mutation
 * (which is reset at once) so neither the query cache nor the mutation cache keeps it, and it dies
 * with the component when the person leaves the tab. A `401` on any of the three calls is a wrong
 * password or code said in place (the routes are exempt from the sign-out redirect, H-F6); a `429`
 * is a wait, on the enrolment and on the disabling alike.
 *
 * Every enrolment attempt carries a number, and leaving the enrolment (Cancelar, a verified code,
 * a new Activar) moves the number on: an answer that arrives for an attempt the person already
 * abandoned is dropped, so a slow `201` can never bring back a QR nobody asked for.
 */

import { useCallback, useRef, useState } from "react";
import type { ReactElement } from "react";

import type { ApiError } from "../../../shared/api/ApiError";
import { useRateLimitWait } from "../../../shared/api/useRateLimitWait";
import { useTranslation } from "../../../shared/i18n";
import type { TotpEnrolment } from "../../../types/api";
import { ErrorState } from "../../../shared/ui/molecules/ErrorState";
import { LoadingState } from "../../../shared/ui/molecules/LoadingState";
import { useToast } from "../../../shared/ui/molecules/ToastProvider";
import { TotpEnrolSteps } from "../components/TotpEnrolSteps";
import type { CodeError } from "../components/TotpEnrolSteps";
import { TotpPanel } from "../components/TotpPanel";
import { useMeQuery } from "../hooks/useMe";
import { useTotp } from "../hooks/useTotp";

type Mode = "idle" | "enrolling";

export function TotpContainer(): ReactElement {
  const { t } = useTranslation();
  const me = useMeQuery();
  const { enrol, verify, disable } = useTotp();
  const { push } = useToast();
  const [mode, setMode] = useState<Mode>("idle");
  const [enrolment, setEnrolment] = useState<TotpEnrolment | null>(null);
  const [wrongPassword, setWrongPassword] = useState(false);
  const [codeError, setCodeError] = useState<CodeError>(null);
  const [limited, setLimited] = useState<ApiError | null>(null);
  const [disableOpen, setDisableOpen] = useState(false);
  const [disableError, setDisableError] = useState<string | null>(null);
  const waitSeconds = useRateLimitWait(limited);
  const attempt = useRef(0);

  /** What a refusal means here: `401` is the person's input, `429` a wait, anything else a notice. */
  const refused = useCallback(
    (error: ApiError, onUnauthorized: () => void): void => {
      if (error.status === 401) onUnauthorized();
      else if (error.status === 429) setLimited(error);
      else push({ tone: "danger", message: t("common.state.error") });
    },
    [push, t],
  );

  /** Forget the current attempt: its secret, its errors, and any answer still on the way. */
  const resetEnrolment = useCallback((): void => {
    attempt.current += 1;
    setEnrolment(null);
    setWrongPassword(false);
    setCodeError(null);
    setLimited(null);
  }, []);

  const leaveEnrolment = useCallback((): void => {
    resetEnrolment();
    setMode("idle");
  }, [resetEnrolment]);

  const startEnrolment = useCallback((): void => {
    resetEnrolment();
    setMode("enrolling");
  }, [resetEnrolment]);

  const submitPassword = useCallback(
    (password: string): void => {
      setWrongPassword(false);
      setLimited(null);
      const mine = attempt.current;
      enrol.mutate(password, {
        onSuccess: (answer) => {
          if (mine !== attempt.current) return;
          setEnrolment(answer);
          // The secret stays in this state only, never in the mutation cache.
          enrol.reset();
        },
        onError: (error) => {
          if (mine !== attempt.current) return;
          refused(error, () => {
            setWrongPassword(true);
          });
        },
      });
    },
    [enrol, refused],
  );

  const submitCode = useCallback(
    (code: string): void => {
      setCodeError(null);
      setLimited(null);
      verify.mutate(code, {
        onSuccess: () => {
          leaveEnrolment();
          push({ tone: "success", message: t("account.totp.activated") });
        },
        onError: (error) => {
          refused(error, () => {
            setCodeError("wrong");
          });
        },
      });
    },
    [verify, refused, leaveEnrolment, push, t],
  );

  const submitDisable = useCallback(
    (code: string): void => {
      setDisableError(null);
      setLimited(null);
      disable.mutate(code, {
        onSuccess: () => {
          setDisableOpen(false);
          push({ tone: "success", message: t("account.totp.disabled") });
        },
        onError: (error) => {
          if (error.status === 401) setDisableError(t("account.totp.codeWrong"));
          else if (error.status === 429) setLimited(error);
          else setDisableError(t("common.state.error"));
        },
      });
    },
    [disable, push, t],
  );

  const copy = useCallback(
    (secret: string): void => {
      const fallback = (): void => {
        push({ tone: "info", message: t("account.totp.copyFailed") });
      };
      try {
        navigator.clipboard.writeText(secret).then(() => {
          push({ tone: "success", message: t("account.totp.copied") });
        }, fallback);
      } catch {
        fallback();
      }
    },
    [push, t],
  );

  if (me.isPending) return <LoadingState label={t("common.state.loading")} />;
  if (me.isError) {
    return (
      <ErrorState
        error={me.error}
        title={t("account.totp.loadError")}
        onRetry={() => {
          void me.refetch();
        }}
      />
    );
  }

  if (mode === "enrolling") {
    return (
      <TotpEnrolSteps
        key={me.data.id}
        enrolment={enrolment}
        busy={enrol.isPending || verify.isPending}
        cancelDisabled={verify.isPending}
        wrongPassword={wrongPassword}
        codeError={codeError}
        waitSeconds={waitSeconds}
        onPassword={submitPassword}
        onVerify={submitCode}
        onCancel={() => {
          leaveEnrolment();
          push({ tone: "info", message: t("account.totp.cancelled") });
        }}
        onCopy={copy}
      />
    );
  }

  return (
    <TotpPanel
      key={me.data.id}
      enabled={me.data.totp_enabled}
      since={me.data.totp_enabled_since}
      lastUsed={me.data.totp_last_used_at}
      onActivate={startEnrolment}
      disableOpen={disableOpen}
      disabling={disable.isPending}
      disableError={disableError}
      waitSeconds={waitSeconds}
      onAskDisable={() => {
        setDisableError(null);
        setLimited(null);
        setDisableOpen(true);
      }}
      onCancelDisable={() => {
        setDisableOpen(false);
      }}
      onDisable={submitDisable}
    />
  );
}
